import { ReactNode, ReactPortal, useEffect } from "react";
import { ReactQueryProvider } from "./ReactQuery";
import { AccountProvider } from "./Account";
import { StoreProvider } from "./Store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useUTM } from "~/hooks/useUTM";
import { heapProjectId } from "~/helpers/environment";
import { usePartnerStack } from "~/hooks/usePartnerStack";
import { useLanguageSync } from "~/hooks/useLanguageSync";

type Props = {
  children: ReactNode | ReactPortal;
};

// Language sync component to be used inside the ReactQueryProvider
const LanguageSyncProvider = ({ children }: Props) => {
  useLanguageSync();
  return <>{children}</>;
};

export const ContextWrapper = ({ children }: Props) => {
  const { init } = usePartnerStack();

  useUTM();

  useEffect(() => {
    if (!heapProjectId) return;

    const scriptContent = `
      window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
      heap.load("${heapProjectId}");
    `;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.innerHTML = scriptContent;

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const script = init();
    if (!script) return;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_ID}>
      <AccountProvider>
        <StoreProvider>
          <ReactQueryProvider>
            <LanguageSyncProvider>{children}</LanguageSyncProvider>
          </ReactQueryProvider>
        </StoreProvider>
      </AccountProvider>
    </GoogleOAuthProvider>
  );
};
